const { collection, limit, orderBy, query } = require('firebase/firestore');
const { db } = require('./firebase');
const { appApiUrl } = require('./api-url');
const { timedFirestoreSubscription } = require('./firestore-timing');

async function loadAccounts(user, search, pageToken) {
  const token = await user.getIdToken();
  const params = new URLSearchParams();

  if (search.trim()) {
    params.set('search', search.trim());
  }
  if (pageToken) {
    params.set('pageToken', pageToken);
  }

  const response = await fetch(`${appApiUrl}/api/admin/accounts?${params}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to load accounts');
  }

  return result;
}

async function loadAdminUsage(user, pageToken) {
  const token = await user.getIdToken();
  const params = new URLSearchParams();
  if (pageToken) params.set('pageToken', pageToken);
  const response = await fetch(`${appApiUrl}/api/admin/usage?${params}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to load platform usage');
  }

  return result;
}

function estimateTimestamp (value) {
  if (!value) return null;
  const date = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}

function subscribePricingEstimates (onUpdate, onError, firestore = db) {
  const estimatesQuery = query(
    collection(firestore, 'pricingEstimateChecks'),
    orderBy('checkedAt', 'desc'),
    limit(20)
  );

  return timedFirestoreSubscription(
    'pricingEstimateChecks (latest 20)',
    estimatesQuery,
    snapshot => onUpdate(snapshot.docs.map(document => {
      const data = document.data();
      return {
        id: document.id,
        domain: data.domain || '',
        checkedAt: estimateTimestamp(data.checkedAt),
        outcome: data.outcome || 'unknown',
        renderedAt: estimateTimestamp(data.renderedAt),
        cacheExpiresAt: estimateTimestamp(data.cacheExpiresAt),
        errorStatus: data.errorStatus || null,
        renderDurationMs: data.renderDurationMs || 0,
        workerComputeMs: data.workerComputeMs || 0
      };
    })),
    onError
  );
}

async function decideAlphaAccess(user, accountId, status) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${accountId}/alpha-access`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ status })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update alpha access');
  }
}

async function loadAccountBilling(user, accountId) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${accountId}/billing`, {
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to load account billing');
  }

  return result;
}

async function loadAccountDetails(user, accountId, pageToken) {
  const token = await user.getIdToken();
  const params = new URLSearchParams();
  if (pageToken) params.set('pageToken', pageToken);
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${encodeURIComponent(accountId)}?${params}`, {
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to load account details');
  }

  return result;
}

async function updateAccountFreeTier(user, accountId, mode, billingTestEnabled, freeTier) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${encodeURIComponent(accountId)}/billing/free-tier`, {
    method: 'POST',
    headers: {
      authorization: `Bearer ${token}`,
      'content-type': 'application/json'
    },
    body: JSON.stringify({ mode, billingTestEnabled, freeTier })
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to update account free tier');
  }

  return result;
}

async function grantAccountDomainTrial(user, accountId) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${encodeURIComponent(accountId)}/domain-trial`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to grant custom-domain trial');
  }

  return result;
}

async function createImpersonationToken(user, accountId) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/accounts/${encodeURIComponent(accountId)}/impersonation-token`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to start impersonation');
  }

  return result;
}

async function createFreshAccountPreview(user) {
  const token = await user.getIdToken();
  const response = await fetch(`${appApiUrl}/api/admin/fresh-account-previews`, {
    method: 'POST',
    headers: { authorization: `Bearer ${token}` }
  });
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to create a temporary account');
  }

  return result;
}

async function deleteFreshAccountPreview(user, accountId) {
  const token = await user.getIdToken();
  const response = await fetch(
    `${appApiUrl}/api/admin/fresh-account-previews/${encodeURIComponent(accountId)}`,
    {
      method: 'DELETE',
      headers: { authorization: `Bearer ${token}` }
    }
  );
  const result = await response.json();

  if (!response.ok) {
    throw new Error(result.error || 'Unable to delete the temporary account');
  }

  return result;
}

module.exports = {
  decideAlphaAccess,
  createImpersonationToken,
  createFreshAccountPreview,
  deleteFreshAccountPreview,
  grantAccountDomainTrial,
  loadAccountBilling,
  loadAccountDetails,
  loadAccounts,
  loadAdminUsage,
  subscribePricingEstimates,
  updateAccountFreeTier
};
